/**
 * How often to ask for a verdict that has not landed yet (`EX-NAT01`).
 *
 * Every page that waits on the judge used to ask every 2 seconds for as long
 * as the verdict took. When the judge falls behind in a big contest, every
 * contestant with a waiting submission asks every 2 seconds at once: 300
 * people is 150 requests a second at the API, exactly when it is busiest.
 *
 * So the wait slows down as it gets longer: every 2 seconds for the first 10
 * seconds (almost every verdict lands here), every 5 seconds until a minute,
 * then every 10 seconds. A fresh submission starts the fast phase again.
 *
 * The age is measured on this device from when this query first saw something
 * waiting, never from the server's timestamp, so a wrong clock on a lab PC
 * cannot make it poll too fast or too slow.
 */

export const PENDING_POLL_FAST_MS = 2_000;
export const PENDING_POLL_MEDIUM_MS = 5_000;
export const PENDING_POLL_SLOW_MS = 10_000;
const FAST_FOR_MS = 10_000;
const MEDIUM_UNTIL_MS = 60_000;

interface Streak {
  since: number;
  waiting: number;
}

const streaks = new WeakMap<object, Streak>();

/**
 * The next poll interval for `query`, or `false` to stop.
 *
 * `waiting` is how many things the query is still waiting on (a submission
 * list's pending rows, or 1 and 0 for one submission). A count that goes up
 * means a new submission, which restarts the fast phase.
 */
export function pendingPollInterval(query: object, waiting: number, now: number = Date.now()): number | false {
  if (waiting <= 0) {
    streaks.delete(query);
    return false;
  }
  const streak = streaks.get(query);
  if (!streak || waiting > streak.waiting) {
    streaks.set(query, { since: now, waiting });
    return PENDING_POLL_FAST_MS;
  }
  streak.waiting = waiting;
  const age = now - streak.since;
  if (age < FAST_FOR_MS) return PENDING_POLL_FAST_MS;
  if (age < MEDIUM_UNTIL_MS) return PENDING_POLL_MEDIUM_MS;
  return PENDING_POLL_SLOW_MS;
}
